import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { ChevronLeft } from 'react-bootstrap-icons';
import { flushSync } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';
import { useTradeStationAuth, useCreateConnectTradeConnection } from '../../hooks/useBackend';
import { hasConnectTradeTradestation, isTradestationDirectBroker } from '../../utils/brokerConnection';
import analyticsService from '../../services/analyticsService';
import { SIGNUP_FUNNEL_EVENTS, startedConnectBrokerProps } from '../../utils/onboardingAnalytics';
import { tradestationBrokerIconUrl } from '../../constants/brokerLogos';
import { BROKER_CONNECT_ACCENT } from '../../constants/brokerConnectColors';
import { ESSENTIALS_ACCENT, PRO_GOLD } from '../billing/billingPlanData';
import {
  getBrokersForConnectPlan,
  isConnectableBroker,
  isPaperOnlyBroker,
  isProEligible,
} from '../../data/shared/connectTradeBrokers';
import BrokerSearchInput from './BrokerSearchInput';

const TS_ID = 'tradestation';

function planFilterLabel(planTier) {
  if (planTier === 'trade' || planTier === 'pro') return 'Trade';
  if (planTier === 'track' || planTier === 'essentials') return 'Track';
  return 'All';
}

function PlanTierSwitch({ value, onChange, colors }) {
  const options = [
    { id: 'all', label: 'All', accent: BROKER_CONNECT_ACCENT },
    { id: 'track', label: 'Track', accent: ESSENTIALS_ACCENT },
    { id: 'trade', label: 'Trade', accent: PRO_GOLD },
  ];

  return (
    <div
      role="group"
      aria-label="Filter brokers by plan"
      style={{
        display: 'inline-flex',
        flexShrink: 0,
        borderRadius: 10,
        border: `1px solid ${colors.border}`,
        background: colors.bgPrimary,
        padding: 3,
        gap: 2,
      }}
    >
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(opt.id)}
            style={{
              border: 'none',
              borderRadius: 8,
              padding: '7px 10px',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              background: active ? opt.accent : 'transparent',
              color: active ? '#0b1220' : colors.textMuted,
              transition: 'background 120ms ease, color 120ms ease',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const planLinkStyle = {
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: 12,
  fontWeight: 600,
  color: BROKER_CONNECT_ACCENT,
  textDecoration: 'underline',
  cursor: 'pointer',
};

function PlanLinksRow({ differenceOpen, onToggleDifference, colors }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Link to="/brokers" style={planLinkStyle}>
          See all supported brokers
        </Link>
        <button
          type="button"
          onClick={onToggleDifference}
          aria-expanded={differenceOpen}
          style={planLinkStyle}
        >
          What&apos;s the difference?
        </button>
      </div>
      {differenceOpen && (
        <p
          style={{
            margin: '8px 0 0',
            fontSize: 12,
            lineHeight: 1.55,
            color: colors.textMuted,
            textAlign: 'left',
          }}
        >
          <strong style={{ color: PRO_GOLD }}>Trade</strong> brokers support live trading and
          market data, plus Track.{' '}
          <strong style={{ color: ESSENTIALS_ACCENT }}>Track</strong> brokers support
          account data, which powers rule alerts, analytics, and account overview.
          Track alerts you the moment you break a rule. Trade stops the order.
          Use the filter to show brokers that fit each.
        </p>
      )}
    </div>
  );
}

/**
 * Single flat list of every broker option — TradeStation (paper→Connect Trade /
 * live→Direct) plus Connect Trade brokers. Shared between ConnectBrokerModal
 * and BrokerConnectPanel.
 */
export default function BrokerConnectList({ onBusyChange }) {
  const { colors } = useTheme();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const { oAuthUrl } = useTradeStationAuth(user?.token);
  const { createConnection } = useCreateConnectTradeConnection();

  const [planTier, setPlanTier] = useState('all');
  const [showPlanDifference, setShowPlanDifference] = useState(false);
  const [query, setQuery] = useState('');
  const [step, setStep] = useState('list'); // 'list' | 'tradestation'
  const [tsDirectLoading, setTsDirectLoading] = useState(false);
  const [ctLoadingBrokerId, setCtLoadingBrokerId] = useState(null);
  const [error, setError] = useState(null);

  const hasCtTradestation = hasConnectTradeTradestation(user);
  const hasTsDirect = isTradestationDirectBroker(user);

  const busy = tsDirectLoading || !!ctLoadingBrokerId;

  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  useEffect(() => {
    const clearConnecting = () => {
      setTsDirectLoading(false);
      setCtLoadingBrokerId(null);
    };
    window.addEventListener('pageshow', clearConnecting);
    return () => window.removeEventListener('pageshow', clearConnecting);
  }, []);

  const connectedCtBrokerIds = useMemo(() => {
    const set = new Set(
      (user?.brokerConnections || [])
        .filter((c) => c.adapter_type === 'connecttrade')
        .map((c) => String(c.broker_name || '').toUpperCase())
        .filter(Boolean)
    );
    if (hasTsDirect) set.add('TRADESTATION');
    return set;
  }, [user?.brokerConnections, hasTsDirect]);

  const handleTradestationDirect = () => {
    if (busy) return;
    if (hasCtTradestation) {
      setError(
        'TradeStation is already linked via Connect Trade. Disconnect that connection before linking a live TradeStation account.'
      );
      return;
    }
    if (hasTsDirect) {
      setError('TradeStation is already connected.');
      return;
    }
    if (!oAuthUrl) {
      setError('TradeStation sign-in is not configured. Check environment variables or contact support.');
      return;
    }
    flushSync(() => {
      setError(null);
      setTsDirectLoading(true);
    });
    analyticsService.trackTradingEvent(
      SIGNUP_FUNNEL_EVENTS.STARTED_CONNECT_BROKER,
      startedConnectBrokerProps({
        broker: 'TradeStation',
        via: 'direct',
        userId: user?.id,
      }),
    );
    window.location.href = oAuthUrl;
  };

  const handleCtBroker = async (broker) => {
    if (busy) return;
    const brokerCode = String(broker.ctCode || broker.id).toUpperCase();
    if (connectedCtBrokerIds.has(brokerCode)) {
      if (brokerCode === 'TRADESTATION' && hasTsDirect) {
        setError(
          'TradeStation is already linked via live Direct. Disconnect it before linking a paper TradeStation account, or choose a different broker.'
        );
      } else {
        setError(`${broker.name} is already connected. Disconnect it first to reconnect.`);
      }
      return;
    }
    flushSync(() => {
      setCtLoadingBrokerId(broker.id);
      setError(null);
    });
    try {
      const res = await createConnection(user.token, {
        broker: brokerCode,
        jwt: user.token,
        return_to: location.pathname,
      });
      if (res?.error) {
        setError(res.error);
        setCtLoadingBrokerId(null);
        return;
      }
      const authUrl =
        res?.authorization_url ||
        res?.connection_request_url ||
        res?.oauth_url;
      if (authUrl) {
        analyticsService.trackTradingEvent(
          SIGNUP_FUNNEL_EVENTS.STARTED_CONNECT_BROKER,
          startedConnectBrokerProps({
            broker: broker.name || brokerCode,
            via: 'connect_trade',
            userId: user?.id,
          }),
        );
        window.location.assign(authUrl);
        return;
      }
      setError('No authorization URL returned from Connect Trade.');
      setCtLoadingBrokerId(null);
    } catch (e) {
      setError(e?.message || 'Failed to start Connect Trade authorization.');
      setCtLoadingBrokerId(null);
    }
  };

  const planBrokers = useMemo(() => getBrokersForConnectPlan(planTier), [planTier]);

  const items = useMemo(() => {
    return planBrokers.map((broker) => {
      const isTs = broker.id === TS_ID;
      const connectable = isConnectableBroker(broker) || isTs;
      const comingSoon = !connectable;
      return {
        ...broker,
        searchName: broker.name,
        enabled: connectable,
        comingSoon,
        alreadyConnected: isTs
          ? hasTsDirect || hasCtTradestation
          : connectedCtBrokerIds.has(String(broker.ctCode).toUpperCase()),
      };
    });
  }, [planBrokers, hasTsDirect, hasCtTradestation, connectedCtBrokerIds]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => item.searchName.toLowerCase().includes(q));
  }, [items, query]);

  const rowStyle = {
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: '14px 16px',
    marginBottom: 10,
    cursor: 'pointer',
    background: colors.bgPrimary,
    textAlign: 'left',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  };

  const connectingRowStyle = {
    ...rowStyle,
    cursor: 'default',
    justifyContent: 'center',
    minHeight: 58,
    opacity: 0.92,
    flexWrap: 'wrap',
  };

  const logoBadgeStyle = {
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: 8,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: colors.bgSecondary,
  };

  const handleRowClick = (item) => {
    if (busy || item.alreadyConnected || item.comingSoon) return;
    if (item.id === TS_ID) {
      setError(null);
      setStep('tradestation');
      return;
    }
    handleCtBroker(item);
  };

  if (step === 'tradestation') {
    const paperBusy = ctLoadingBrokerId === TS_ID;
    const liveBusy = tsDirectLoading;
    const paperConnected = hasCtTradestation;
    const liveConnected = hasTsDirect;

    return (
      <div style={{ width: '100%' }}>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            if (busy) return;
            setStep('list');
            setError(null);
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'none',
            border: 'none',
            color: colors.textMuted,
            fontSize: 13,
            fontWeight: 600,
            padding: '0 0 14px',
            cursor: busy ? 'default' : 'pointer',
          }}
        >
          <ChevronLeft size={16} aria-hidden />
          All brokers
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={logoBadgeStyle}>
            <img
              src={tradestationBrokerIconUrl()}
              alt="TradeStation"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
          <div>
            <div style={{ fontWeight: 700, color: colors.text }}>TradeStation</div>
            <div style={{ fontSize: 12, color: colors.textMuted }}>
              Choose paper (Connect Trade) or live (Direct)
            </div>
          </div>
        </div>

        {error && (
          <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 14 }}>{error}</div>
        )}

        {paperBusy ? (
          <div style={connectingRowStyle} aria-busy="true" aria-live="polite">
            <Spinner size="sm" animation="border" />
            <span style={{ fontWeight: 600, color: colors.text }}>
              Connecting to TradeStation paper…
            </span>
          </div>
        ) : (
          <button
            type="button"
            disabled={busy || paperConnected}
            style={{
              ...rowStyle,
              opacity: paperConnected ? 0.55 : 1,
              cursor: paperConnected || busy ? 'default' : 'pointer',
            }}
            onClick={() => handleCtBroker({
              id: TS_ID,
              ctCode: 'TRADESTATION',
              name: 'TradeStation',
            })}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: colors.text }}>Paper account</div>
              <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
                Simulated trading via Connect Trade
              </div>
              {paperConnected && (
                <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
                  Already connected
                </div>
              )}
            </div>
          </button>
        )}

        {liveBusy ? (
          <div style={connectingRowStyle} aria-busy="true" aria-live="polite">
            <Spinner size="sm" animation="border" />
            <span style={{ fontWeight: 600, color: colors.text }}>
              Connecting to TradeStation live…
            </span>
          </div>
        ) : (
          <button
            type="button"
            disabled={busy || liveConnected}
            style={{
              ...rowStyle,
              opacity: liveConnected ? 0.55 : 1,
              cursor: liveConnected || busy ? 'default' : 'pointer',
            }}
            onClick={handleTradestationDirect}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: colors.text }}>Live account</div>
              <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
                Connect directly to TradeStation
              </div>
              {liveConnected && (
                <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
                  Already connected
                </div>
              )}
            </div>
          </button>
        )}

        <div
          style={{
            fontSize: 11,
            color: colors.textMuted,
            textAlign: 'center',
            marginTop: 12,
            lineHeight: 1.5,
          }}
        >
          We use{' '}
          <span style={{ color: BROKER_CONNECT_ACCENT, fontWeight: 700 }}>Connect Trade</span>
          {' '}to connect paper TradeStation accounts through a secure connection.
        </div>
      </div>
    );
  }

  return (
    <div className="broker-connect-list" style={{ width: '100%' }}>
      <PlanLinksRow
        differenceOpen={showPlanDifference}
        onToggleDifference={() => setShowPlanDifference((v) => !v)}
        colors={colors}
      />

      <BrokerSearchInput
        value={query}
        onChange={setQuery}
        trailing={(
          <PlanTierSwitch
            value={planTier}
            onChange={(next) => {
              setPlanTier(next);
              setError(null);
            }}
            colors={colors}
          />
        )}
      />

      {error && (
        <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 14 }}>{error}</div>
      )}

      {filteredItems.length === 0 && (
        <div style={{ color: colors.textMuted, fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
          No brokers match your search
          {planTier === 'all' ? '.' : ` for ${planFilterLabel(planTier)}.`}
        </div>
      )}

      <div className="broker-connect-list-scroll">
        {filteredItems.map((item) => {
          const isTs = item.id === TS_ID;
          const isConnecting = isTs
            ? false
            : ctLoadingBrokerId === item.id;
          const alreadyConnected = item.alreadyConnected;
          const fitContain = item.logoFit === 'contain' || isTs;
          const showProBadge =
            planTier !== 'track' && planTier !== 'essentials'
            && isProEligible(item);

          if (isConnecting) {
            return (
              <div key={item.id} style={connectingRowStyle} aria-busy="true" aria-live="polite">
                <Spinner size="sm" animation="border" />
                <span style={{ fontWeight: 600, color: colors.text }}>
                  Connecting to {item.name}…
                </span>
              </div>
            );
          }

          const disabled = !item.enabled || busy || alreadyConnected;
          const paperOnly = isPaperOnlyBroker(item);

          return (
            <button
              key={item.id}
              type="button"
              disabled={disabled}
              style={{
                ...rowStyle,
                opacity: disabled ? 0.55 : 1,
                cursor: disabled ? 'default' : 'pointer',
              }}
              onClick={() => handleRowClick(item)}
            >
              <div
                style={{
                  ...logoBadgeStyle,
                  background: item.logoBg === 'dark' ? '#0f172a' : colors.bgSecondary,
                }}
              >
                <img
                  src={isTs ? tradestationBrokerIconUrl() : item.logoSrc}
                  alt={item.logoAlt || item.name}
                  style={{
                    width: fitContain && !isTs ? 28 : '100%',
                    height: fitContain && !isTs ? 28 : '100%',
                    objectFit: isTs ? 'cover' : (item.logoFit === 'contain' ? 'contain' : 'cover'),
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, color: colors.text }}>{item.name}</span>
                  {paperOnly && (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 6px',
                        borderRadius: 10,
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        backgroundColor: colors.warning,
                        color: '#fff',
                        lineHeight: 1.2,
                      }}
                    >
                      Paper
                    </span>
                  )}
                  {showProBadge && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: PRO_GOLD }}>Trade</span>
                  )}
                </div>
                {isTs && !alreadyConnected && item.enabled && (
                  <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
                    Paper or live account
                  </div>
                )}
                {item.comingSoon ? (
                  <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>Coming soon</div>
                ) : paperOnly && !alreadyConnected ? (
                  <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
                    Live Trading Coming Soon
                  </div>
                ) : alreadyConnected ? (
                  <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
                    Already connected
                  </div>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>

      <div className="broker-connect-list-footer">
        <div
          style={{
            fontSize: 11,
            color: colors.textMuted,
            textAlign: 'center',
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          We use{' '}
          <span style={{ color: BROKER_CONNECT_ACCENT, fontWeight: 700 }}>Connect Trade</span>
          {' '}to connect to your broker account through a secure connection.
        </div>
      </div>
    </div>
  );
}
